// The season chronicle book in the hub (chronicle.md §9).
//
// The book is a page inside RECORD, the way an achievement category is. It
// shows one volume at a time:
//
//   - the volume row: one press per volume, newest first, and the press
//     that picks one;
//   - the share row of the volume: the switch for a closed volume, and the
//     reason for a live one (R1168);
//   - the season page, where the volume has one, with its MOMENTS strip;
//   - the day pages, newest first. A moment line carries a mark.
//
// The volume bar stands at the bottom of the pane, in the thumb zone of a
// telephone (ui-map-interface.md §12): ‹ and › step to the older and the
// newer volume, and a press on the open volume opens the volume list. The
// list is one row per volume in a box that scrolls past
// `recordVolumeRows` rows, so a long chronicle never fills the pane; the
// VOLUMES column of a wide pane draws the same list (R1920). The way back
// is the ‹ RECORD bar that RECORD sets at the top of the page (R1751).
//
// A battle line of a LIVE volume carries a REPLAY link (chronicle.md §8,
// R1181). The hub opens no world socket, so the link is an address of the
// game page: it joins the world and asks for the film there. A closed
// volume shows no link, because the roll deletes the combat log (R1169).
//
// A shared volume carries a COPY LINK press beside the switch. It hands
// the address of the public reader (`reader.ts`) on through `ui/share.ts`;
// a browser that refuses both ways shows the address, and the owner
// selects it by hand.
//
// Every read goes through the hub cache. `render.ts` writes the words; this
// module writes the markup and handles the presses.

import { button, linkButton, pillGroup } from '../../choice/choice';
import {
  getMyChronicle, getMyChronicleVolumes, setChronicleShared,
  type ChronicleVolume, type ChronicleVolumePages,
} from '../../../net/api';
import { chronicle as C } from '../../../gen/strings';
import { chronicleNoun } from '../../../gen/nouns';
import { CHROME_CHRONICLE_COPIED_MS } from '../../../gen/presentation';
import { shareOutcome } from '../../share';
import { TTL_ON_OPEN, type Cell } from '../hubstate';
import {
  card, tagCard, empty, esc, failed, lede, pending, plain, type PanelContext,
} from '../panel';
import {
  renderDay, renderSeason, voiceLookup, type ChronicleDayView, type ChronicleLine,
  type ChronicleScope, type ChronicleSeasonView, type SeasonSection, type VoiceLookup,
} from './render';
import { pageBase, readerHref, replayHref } from './address';
import { wardroomCell } from '../panels/wardroom';

/** The cache key of the volume list. The RECORD card reads the same cell. */
const VOLUMES_KEY = 'chronicle:volumes';

/** The cache key of one volume's pages. */
function volumeKey(world: number, season: number): string {
  return `chronicle:vol:${world}:${season}`;
}

/** The volume list, through the hub cache. */
export function volumesCell(ctx: PanelContext): Cell<ChronicleVolume[]> {
  return ctx.data.cell(VOLUMES_KEY, TTL_ON_OPEN, getMyChronicleVolumes);
}

/** One volume's pages, through the hub cache. */
export function volumeCell(
  ctx: PanelContext, world: number, season: number,
): Cell<ChronicleVolumePages> {
  return ctx.data.cell(volumeKey(world, season), TTL_ON_OPEN,
    () => getMyChronicle(world, season));
}

/** The seed scope of one volume for the signed-in account. */
export function scopeOf(ctx: PanelContext, v: ChronicleVolumePages): ChronicleScope {
  return {
    account: ctx.me().account.id,
    world: v.world,
    season: v.season,
    worldName: v.worldName,
  };
}

/**
 * The voice lines of the signed-in account for one volume, by captain id
 * (chronicle.md §4.3, R1240, R1244). They come from the WARDROOM read of
 * the roster the volume names: an event volume reads the event roster, a
 * core volume the core roster. The cell is the one the WARDROOM panel
 * reads too, so the book costs no second fetch of a roster. A roster of
 * `none` reads nothing. Until the read lands, and where it fails, every
 * remark takes its default pool.
 */
export function ownVoices(
  ctx: Pick<PanelContext, 'data'>, v: Pick<ChronicleVolumePages, 'roster'>,
): VoiceLookup | undefined {
  if (v.roster === 'none') return undefined;
  const w = wardroomCell(ctx, v.roster).value;
  return w === undefined ? undefined : voiceLookup(w.captains);
}

/** The day pages of a volume, rendered, newest first. `voices` is the
 *  owner's lookup; the public reader passes none. */
export function dayViews(
  v: ChronicleVolumePages, scope: ChronicleScope, voices?: VoiceLookup,
): ChronicleDayView[] {
  return v.pages
    .filter((p) => p.day > 0)
    .sort((a, b) => b.day - a.day)
    .map((p) => renderDay(p, scope, C, chronicleNoun, voices));
}

/** The label of a season section. The summary stands under the header. */
const SECTION_LABEL: Readonly<Record<SeasonSection, string>> = {
  summary: '',
  captains: C.label_captains,
  tech: C.label_tech,
  ledger: C.label_ledger,
  milestones: C.label_milestones,
};

/** One line of a page as markup. A moment carries the mark. */
export function lineHtml(l: ChronicleLine): string {
  return bookLineHtml(l, '');
}

/** One line of a page with its REPLAY link, or with none for an empty
 *  address. The link is an anchor and no `act`: the press leaves the hub
 *  for the game page (chronicle.md §8). */
function bookLineHtml(l: ChronicleLine, replay: string): string {
  const mark = l.moment
    ? `<span class="mm" title="${esc(C.moment_mark)}" aria-label="${esc(C.moment_mark)}">◆</span>`
    : '';
  const link = replay === ''
    ? ''
    : linkButton(C.replay, replay, { variant: 'primary', size: 'compact', cls: 'rl' });
  return `<div class="cl${l.moment ? ' moment' : ''}${l.kind === 'close' ? ' close' : ''}">`
    + `${mark}<span class="t">${esc(l.text)}</span>${link}</div>`;
}

/** One day page as a card. `replayWorld` is the world of a live volume,
 *  whose battle lines carry a REPLAY link, and 0 for a volume that shows
 *  none: a closed one, and every public read (R1169). `onMap` is the
 *  markup of ▸ ON THE MAP, or empty for none (R1620). */
export function dayCard(d: ChronicleDayView, replayWorld = 0, onMap = ''): string {
  const line = (l: ChronicleLine): string =>
    bookLineHtml(l, replayWorld > 0 ? replayHref(pageBase(), replayWorld, l.fact?.replay) : '');
  const body = `<div class="chron">${d.lines.map(line).join('')}${lineHtml(d.close)}</div>`;
  // The public reader puts ▸ ON THE MAP on the right of the head when the
  // volume carries a season timelapse (R1620); the book puts nothing.
  return onMap === '' ? card(d.header, '▤', '', body) : tagCard(d.header, '▤', onMap, body);
}

/** The season page as a card. */
export function seasonCard(s: ChronicleSeasonView): string {
  let body = '<div class="chron">';
  for (const sec of s.sections) {
    if (SECTION_LABEL[sec.id] !== '') body += `<div class="cs">${esc(SECTION_LABEL[sec.id])}</div>`;
    body += sec.lines.map(lineHtml).join('');
  }
  if (s.moments.length > 0) {
    body += `<div class="cs">${esc(C.moments)}</div>`;
    body += s.moments.map((m) => `<div class="cl moment"><span class="mm">◆</span>`
      + `<span class="d">${esc(C.moment_day({ day: m.day }))}</span>`
      + `<span class="t">${esc(m.text)}</span></div>`).join('');
  }
  return card(s.header, '✦', '', `${body}</div>`);
}

/** The key of one volume for the COPY LINK state. */
function copyKey(v: ChronicleVolume): string {
  return `${v.world}:${v.season}`;
}

/** The book. It keeps which volume is open and the state of a share press. */
export class ChronicleBook {
  /** The open volume, or undefined for the newest. */
  private pick?: { world: number; season: number };
  /** The world whose live volume opens when nothing is picked, or 0: the
   *  way back from a replay link (R1684). */
  private liveWorld = 0;
  private sharing = false;
  private shareError: unknown;
  /** The answer of the last COPY LINK press, for the volume `key` names:
   *  `copied` after a clipboard write, `hand` while the address stands in
   *  a box to select by hand. */
  private copy?: { key: string; state: 'copied' | 'hand'; href: string };
  private copyTimer?: ReturnType<typeof setTimeout>;
  /** True while the volume list of the bar stands open. */
  private listOpen = false;
  /** The scroll of the volume list, kept over a rewrite of the pane, or
   *  undefined to bring the open volume into view. */
  private listTop?: number;

  constructor(private ctx: PanelContext) {}

  /** Opens the book on the newest volume. */
  reset(): void {
    this.pick = undefined;
    this.liveWorld = 0;
    this.listOpen = false;
    this.listTop = undefined;
    this.sharing = false;
    this.shareError = undefined;
    this.clearCopy();
  }

  private clearCopy(): void {
    if (this.copyTimer !== undefined) clearTimeout(this.copyTimer);
    this.copyTimer = undefined;
    this.copy = undefined;
  }

  /** Opens the book on the live volume of one world, or on the newest
   *  when that world has none. */
  openOn(world: number): void {
    this.liveWorld = world;
  }

  /** The volume the book shows from `list`: the picked one, else the live
   *  volume of the world the book opened on, else the newest. The VOLUMES
   *  column of RECORD marks it (ui-map-interface.md §16.3). */
  current(list: ChronicleVolume[]): ChronicleVolume | undefined {
    const p = this.pick;
    const w = this.liveWorld;
    return (p && list.find((v) => v.world === p.world && v.season === p.season))
      || (w > 0 ? list.find((v) => v.live && v.world === w) : undefined)
      || list[0];
  }

  /** The volume list: one row per volume, newest first, the open one
   *  marked. The box scrolls past `recordVolumeRows` rows (R1920). The
   *  VOLUMES column of RECORD and the bar draw it. */
  list(list: ChronicleVolume[]): string {
    const on = this.current(list);
    return '<div class="vollist">' + list.map((v) => {
      const label = C.volume_label({ season: v.season, world: v.worldName });
      return `<button type="button" class="vpick vrow${v === on ? ' on' : ''}" data-act="chrvol"`
        + ` data-world="${v.world}" data-season="${v.season}">`
        + `<span class="vl">${esc(label)}</span>`
        + `<span class="vt">${esc(v.live ? C.live_tag : C.closed_tag)}</span></button>`;
    }).join('') + '</div>';
  }

  /** The bar at the foot of the pane: ‹ older, the open volume with its
   *  place, › newer, and the volume list over them while it stands open.
   *  The way back is the ‹ RECORD bar at the top of the page (R1751), so
   *  a book with no volume has no bar. */
  private bar(list: ChronicleVolume[], on?: ChronicleVolume): string {
    if (list.length === 0) return '';
    const at = on === undefined ? -1 : list.indexOf(on);
    const step = (glyph: string, by: number, label: string): string => {
      const to = list[at + by];
      return to === undefined || at < 0
        ? `<span class="vpick step" aria-hidden="true">${glyph}</span>`
        : `<button type="button" class="vpick step" data-act="chrstep" data-step="${by}"`
          + ` aria-label="${esc(label)}">${glyph}</button>`;
    };
    const name = on === undefined ? '' : C.volume_label({ season: on.season, world: on.worldName });
    const place = at < 0 ? '' : C.volume_place({ at: at + 1, of: list.length });
    const cur = `<button type="button" class="vpick cur${this.listOpen ? ' on' : ''}" data-act="chrlist"`
      + ` aria-expanded="${this.listOpen}"><span class="vl">${esc(name)}</span>`
      + `<span class="vt">${esc(place)} ${this.listOpen ? '▴' : '▾'}</span></button>`;
    return '<div class="chronbar">'
      + (this.listOpen ? this.list(list) : '')
      + `<div class="chronstep">${step('‹', 1, C.volume_older)}${cur}${step('›', -1, C.volume_newer)}</div>`
      + '</div>';
  }

  /** Runs after a rewrite of the pane: each visible volume list takes
   *  back its scroll, or brings the open volume into view, and keeps
   *  its scroll for the next rewrite. */
  wire(root: HTMLElement): void {
    for (const box of root.querySelectorAll<HTMLElement>('.vollist')) {
      if (box.clientHeight === 0) continue;
      const row = box.querySelector<HTMLElement>('.vrow.on');
      if (this.listTop !== undefined) {
        box.scrollTop = this.listTop;
      } else if (row !== null) {
        box.scrollTop = row.offsetTop - (box.clientHeight - row.offsetHeight) / 2;
      }
      this.listTop = box.scrollTop;
      box.addEventListener('scroll', () => { this.listTop = box.scrollTop; }, { passive: true });
    }
  }

  /** The share row of one volume (R1168). */
  private shareRow(v: ChronicleVolume, pages: ChronicleVolumePages): string {
    const tag = v.live ? C.live_tag : C.closed_tag;
    if (v.live) return `<div class="chronshare"><span class="tag">${esc(tag)}</span>${lede(C.share_live)}</div>`;
    const shared = pages.shared ?? v.shared;
    // Shared or private is one saved value of the volume (R2011).
    const press = pillGroup([
      { id: 'private', label: C.share_off, locked: this.sharing },
      { id: 'shared', label: C.share_on, locked: this.sharing },
    ], shared ? 'shared' : 'private', 'chrshare');
    return `<div class="chronshare"><span class="tag">${esc(tag)}</span>${press}`
      + (shared ? this.copyPress(v) : '')
      + `${lede(C.share_what)}</div>`
      + (shared ? this.copyByHand(v) : '')
      + (this.shareError === undefined ? '' : failed(this.shareError));
  }

  /** The COPY LINK press of a shared volume. It copies and changes
   *  nothing, so it takes one press (R837 arms destructive controls). */
  private copyPress(v: ChronicleVolume): string {
    const c = this.copy;
    const done = c !== undefined && c.key === copyKey(v) && c.state === 'copied';
    return button(done ? C.link_copied : C.copy_link, { act: 'chrcopy' }, { variant: 'primary', on: done });
  }

  /** The address to select by hand, where the clipboard refused it. */
  private copyByHand(v: ChronicleVolume): string {
    const c = this.copy;
    if (c === undefined || c.key !== copyKey(v) || c.state !== 'hand') return '';
    return `<div class="chroncopy"><span>${esc(C.copy_by_hand)}</span>`
      + `<input type="text" readonly value="${esc(c.href)}" aria-label="${esc(C.copy_link)}"></div>`;
  }

  /** The whole book, as the blocks of one page. `bar` false draws no
   *  volume bar: a wide pane of RECORD lists the volumes in its VOLUMES
   *  column (§16.3). */
  html(o: { bar?: boolean } = {}): string {
    const withBar = o.bar ?? true;
    const cell = volumesCell(this.ctx);
    const list = cell.value;
    const bar = (l: ChronicleVolume[], on?: ChronicleVolume): string => (withBar ? this.bar(l, on) : '');
    // A state of the whole book is the page itself, with no box and no
    // second title under the page title CHRONICLE (R1945).
    if (list === undefined) return plain(pending(cell)) + bar([]);
    const v = this.current(list);
    if (!v) return plain(empty(C.no_volume, C.no_volume_why)) + bar(list);
    const pc = volumeCell(this.ctx, v.world, v.season);
    const pages = pc.value;
    if (pages === undefined) {
      return plain(lede(C.volume_label({ season: v.season, world: v.worldName })) + pending(pc))
        + bar(list, v);
    }
    const scope = scopeOf(this.ctx, pages);
    const voices = ownVoices(this.ctx, pages);
    const days = dayViews(pages, scope, voices);
    const seasonPage = pages.pages.find((p) => p.day === 0);
    let out = this.shareRow(v, pages);
    if (seasonPage) out += seasonCard(renderSeason(seasonPage, days, scope, C, chronicleNoun, voices));
    out += days.map((d) => dayCard(d, v.live ? v.world : 0)).join('');
    if (!seasonPage && days.length === 0) out += plain(empty(C.no_page));
    return out + bar(list, v);
  }

  /** The volume list as the cache holds it now, and the volume open in
   *  it, for a press that acts on what the pane drew. */
  private peeked(): { list: ChronicleVolume[]; open: ChronicleVolume | undefined } {
    const list = this.ctx.data.peek<ChronicleVolume[]>(VOLUMES_KEY).value ?? [];
    return { list, open: this.current(list) };
  }

  /** One press. True where the book took the action. */
  act(action: string, el: HTMLElement): boolean {
    if (action === 'chrvol') {
      this.ctx.play('sfx-menu-press');
      this.pick = {
        world: Number(el.dataset.world ?? 0),
        season: Number(el.dataset.season ?? 0),
      };
      this.listOpen = false;
      this.shareError = undefined;
      this.ctx.refresh();
      return true;
    }
    if (action === 'chrstep') {
      const { list, open: on } = this.peeked();
      const to = on === undefined ? undefined : list[list.indexOf(on) + Number(el.dataset.step ?? 0)];
      if (to === undefined) return true;
      this.ctx.play('sfx-menu-press');
      this.pick = { world: to.world, season: to.season };
      this.listTop = undefined;
      this.shareError = undefined;
      this.ctx.refresh();
      return true;
    }
    if (action === 'chrlist') {
      this.ctx.play('sfx-menu-press');
      this.listOpen = !this.listOpen;
      this.listTop = undefined;
      this.ctx.refresh();
      return true;
    }
    if (action === 'chrshare') {
      this.share(el.dataset.id === 'shared');
      return true;
    }
    if (action === 'chrcopy') {
      this.copyLink();
      return true;
    }
    return false;
  }

  /** Hands the public reader's address of the open volume on through
   *  `ui/share.ts` (R1206): the share sheet on a telephone, the clipboard
   *  elsewhere. A sheet that took it needs no label of ours. A browser
   *  where both ways refused gets the address in a box to select by
   *  hand. */
  private copyLink(): void {
    const v = this.peeked().open;
    if (!v || v.live) return;
    this.ctx.play('sfx-menu-press');
    const key = copyKey(v);
    const href = readerHref(pageBase(), {
      publicId: this.ctx.me().account.id, world: v.world, season: v.season,
    });
    this.clearCopy();
    void shareOutcome(href, 'link').then((out) => {
      if (out === 'failed') {
        this.copy = { key, state: 'hand', href };
      } else if (out === 'copied') {
        this.copy = { key, state: 'copied', href };
        this.copyTimer = setTimeout(() => {
          this.copyTimer = undefined;
          if (this.copy?.state === 'copied') this.copy = undefined;
          this.ctx.refresh();
        }, CHROME_CHRONICLE_COPIED_MS);
      }
      this.ctx.refresh();
    });
  }

  /** Flips the share state of the open volume, then reads both cells
   *  again, so the pill group shows what the server stored. */
  private share(next: boolean): void {
    const v = this.peeked().open;
    if (!v || v.live || this.sharing) return;
    const pages = this.ctx.data.peek<ChronicleVolumePages>(volumeKey(v.world, v.season)).value;
    if (next === (pages?.shared ?? v.shared)) return;
    this.ctx.play('sfx-menu-press');
    this.sharing = true;
    this.shareError = undefined;
    this.clearCopy();
    this.ctx.refresh();
    setChronicleShared(v.world, v.season, next).then(
      () => { this.sharing = false; },
      (err: unknown) => { this.sharing = false; this.shareError = err; },
    ).finally(() => {
      this.ctx.data.expire(VOLUMES_KEY);
      this.ctx.data.expire(volumeKey(v.world, v.season));
      this.ctx.refresh();
    });
  }
}
